import dayjs, { Dayjs } from "dayjs";

/**
 * ✅ Check if any date in range overlaps with a holiday.
 * Only blocks holidays with type "fixed" or "National_holiday".
 */
export const isHolidayInRange = (
  fromDate: Dayjs,
  toDate: Dayjs,
  holidays: any[]
): boolean => {
  if (!holidays?.length) return false;

  const start = fromDate.startOf("day");
  const end = toDate.endOf("day");

  return holidays.some((holiday) => {
    if (holiday.Status?.toLowerCase() !== "active") return false;

    const type = String(holiday.HolidayType || "").toLowerCase();
    if (type !== "fixed" && type !== "national_holiday") return false;

    const holidayStart = dayjs(holiday.StartDate).startOf("day");
    const holidayEnd = dayjs(holiday.EndDate || holiday.StartDate).endOf("day");

    // Overlapping condition
    return (
      holidayStart.isSameOrBefore(end) &&
      holidayEnd.isSameOrAfter(start)
    );
  });
};


// ✅ Safely normalize WeekPattern JSON
export const normalizeWeekPattern = (raw: any) => {
  if (!raw) return {};
  if (typeof raw === "string") {
    try { return JSON.parse(raw); } catch { return {}; }
  }
  return raw;
};

// ✅ Parse AppliedTo values into an array of numeric IDs
const toIdArray = (appliedTo: any): number[] => {
  if (appliedTo == null) return [];
  try {
    const parsed = typeof appliedTo === "string" ? JSON.parse(appliedTo) : appliedTo;
    if (Array.isArray(parsed)) return parsed.map((x) => Number(x));
  } catch {
    const n = Number(appliedTo);
    if (Number.isFinite(n)) return [n];
    return [];
  }
  const n = Number(appliedTo);
  return Number.isFinite(n) ? [n] : [];
};

// ✅ Add weekly pattern into index
const pushWeeks = (
  index: Map<number, Map<string, Set<number>>>,
  id: number,
  wp: any
) => {
  const current = index.get(id) ?? new Map<string, Set<number>>();
  Object.entries(wp).forEach(([day, row]: any) => {
    const set = current.get(day) ?? new Set<number>();
    if (row?.all) [1, 2, 3, 4, 5].forEach((w) => set.add(w));
    else if (row?.weeks) {
      Object.entries(row.weeks).forEach(([wk, val]: any) => {
        if (val) set.add(Number(wk));
      });
    }
    if (set.size) current.set(day, set);
  });
  index.set(id, current);
};

/**
 * ✅ Build employee weekly off map.
 * Handles Employee > Department > Branch priority
 */
export const buildEmployeeWeeklyOffMap = (
  weekoff: any[],
  employeesbyUser: any
): Record<number, Map<string, Set<number>>> => {
  if (!employeesbyUser) return {};

  const { employeeId, DepartmentId, BranchId } = employeesbyUser;
  const empIdNum = Number(employeeId);
  const deptIdNum = Number(DepartmentId);
  const branchIdNum = Number(BranchId);

  const empIndex = new Map<number, Map<string, Set<number>>>();
  const deptIndex = new Map<number, Map<string, Set<number>>>();
  const branchIndex = new Map<number, Map<string, Set<number>>>();

  (weekoff ?? []).forEach((item: any) => {
    if (!item || item?.Status?.toLowerCase() !== "active") return;

    const ids = toIdArray(item.AppliedTo);
    const wp = normalizeWeekPattern(item.WeekPattern);
    const type = String(item.Type || "").toLowerCase();

    if (type === "employee") ids.forEach((id) => pushWeeks(empIndex, id, wp));
    else if (type === "department") ids.forEach((id) => pushWeeks(deptIndex, id, wp));
    else if (type === "branch") ids.forEach((id) => pushWeeks(branchIndex, id, wp));
  });

  const empWeeks = empIndex.get(empIdNum);
  const deptWeeks = deptIndex.get(deptIdNum);
  const branchWeeks = branchIndex.get(branchIdNum);

  const result: Record<number, Map<string, Set<number>>> = {};

  if (empWeeks) result[empIdNum] = empWeeks;
  else if (deptWeeks) result[empIdNum] = deptWeeks;
  else if (branchWeeks) result[empIdNum] = branchWeeks;
  else result[empIdNum] = new Map();

  return result;
};

/**
 * ✅ Check if specific date is a weekly off for given employee
 */
export const isWeeklyOffDate = (
  date: Dayjs,
  employeeId: number,
  employeeWeeklyOffMap: Record<number, Map<string, Set<number>>>
): boolean => {
  const dayLabel = date.format("dddd");
  const weekIndex = Math.floor((date.date() - 1) / 7) + 1;
  const empWeeks = employeeWeeklyOffMap[employeeId];
  return empWeeks?.get(dayLabel)?.has(weekIndex) ?? false;
};
